import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Users, Handshake, TrendingUp, Globe, CheckCircle, ArrowRight } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
const services = [{
  icon: Users,
  title: 'Investor & Capital Matchmaking',
  description: 'Connect early-stage explorers, developers, and mine operators with qualified investors seeking exposure to high-potential natural resources and precious metals opportunities.',
  benefits: ['Access to qualified investor networks', 'Strategic capital alignment', 'Structured investment frameworks', 'Long-term partnership facilitation'],
  idealFor: ['Early-stage mining explorers', 'Mine developers seeking capital', 'Qualified investors seeking exposure to precious metals', 'Family offices and institutional investors']
}, {
  icon: Handshake,
  title: 'Strategic Off-Take Agreements',
  description: 'Help mining projects secure long-term revenue channels through structured relationships with buyers in international markets, particularly in the Middle East.',
  benefits: ['Guaranteed revenue streams', 'Access to international buyers', 'Structured off-take frameworks', 'Risk mitigation through long-term agreements'],
  idealFor: ['Mine operators seeking revenue certainty', 'Development-stage projects', 'Commodity buyers seeking reliable supply', 'Projects targeting Middle Eastern markets']
}, {
  icon: TrendingUp,
  title: 'Mining Project Partnership Facilitation',
  description: 'Facilitate strategic relationships between project owners, operators, capital partners, and commodity buyers to create mutually beneficial partnerships.',
  benefits: ['Strategic partner identification', 'Partnership structuring expertise', 'Multi-stakeholder alignment', 'Value creation across the supply chain'],
  idealFor: ['Mining project owners', 'Operators seeking strategic partners', 'Capital partners seeking operational expertise', 'Buyers seeking upstream integration']
}, {
  icon: Globe,
  title: 'Global Market Access',
  description: 'Leverage international relationships and market knowledge to unlock cross-border opportunities in key commodity trading and investment hubs.',
  benefits: ['International network access', 'Cross-border transaction expertise', 'Market intelligence and insights', 'Regulatory and cultural navigation'],
  idealFor: ['Companies seeking international expansion', 'Investors targeting global opportunities', 'Projects requiring cross-border capital', 'Buyers seeking international supply sources']
}, {
  icon: CheckCircle,
  title: 'End-to-End Advisory Support',
  description: 'Bridge investment, exploration, production, and distribution with coordinated strategic guidance across the precious metals supply chain.',
  benefits: ['Comprehensive supply chain expertise', 'Strategic planning and execution', 'Ongoing advisory support', 'Value optimization across all stages'],
  idealFor: ['Complex multi-stakeholder projects', 'Companies seeking integrated solutions', 'Strategic partnerships requiring coordination', 'Long-term value creation initiatives']
}];
export function ServicesPage() {
  return <div className="w-full pt-20" data-id="element-411">
      {/* Hero */}
      <section className="bg-navy py-16 md:py-20" data-id="element-412">
        <div className="container mx-auto px-4 lg:px-8" data-id="element-413">
          <motion.div className="max-w-4xl mx-auto text-center" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-414">
            <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4 md:mb-6" data-id="element-415">
              Our Services
            </h1>
            <div className="w-20 md:w-24 h-1 bg-gold mx-auto mb-6 md:mb-8" data-id="element-416" />
            <p className="text-lg md:text-xl text-steel leading-relaxed max-w-2xl mx-auto" data-id="element-417">
              Comprehensive advisory solutions across the precious metals supply
              chain, connecting capital, commodities, and opportunity.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services */}
      <section className="bg-white py-16 md:py-20" data-id="element-418">
        <div className="container mx-auto px-4 lg:px-8" data-id="element-419">
          <div className="space-y-10 md:space-y-16 lg:space-y-20" data-id="element-420">
            {services.map((service, index) => <motion.div key={index} className="max-w-6xl mx-auto" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} data-id="element-421">
                <Card className="border border-gold/20 hover:border-gold transition-all overflow-hidden" data-id="element-422">
                  <CardContent className="p-0" data-id="element-423">
                    <div className="grid grid-cols-1 lg:grid-cols-3" data-id="element-424">
                      {/* Icon & Title */}
                      <div className="bg-navy p-6 sm:p-8 lg:p-12 flex flex-col justify-center" data-id="element-425">
                        <service.icon className="h-12 w-12 md:h-16 md:w-16 text-gold mb-4 md:mb-6" data-id="element-426" />
                        <h2 className="font-heading text-xl sm:text-2xl md:text-3xl font-bold text-white mb-3 md:mb-4" data-id="element-427">
                          {service.title}
                        </h2>
                        <p className="text-steel leading-relaxed text-sm md:text-base" data-id="element-428">
                          {service.description}
                        </p>
                      </div>

                      {/* Details */}
                      <div className="lg:col-span-2 p-6 sm:p-8 lg:p-12 bg-ivory" data-id="element-429">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 md:gap-8" data-id="element-430">
                          {/* Benefits */}
                          <div data-id="element-431">
                            <h3 className="font-heading text-lg md:text-xl font-semibold text-navy mb-3 md:mb-4 flex items-center" data-id="element-432">
                              <CheckCircle className="h-5 w-5 text-gold mr-2 flex-shrink-0" data-id="element-433" />
                              Key Benefits
                            </h3>
                            <ul className="space-y-2 md:space-y-3" data-id="element-434">
                              {service.benefits.map((benefit, i) => <li key={i} className="flex items-start text-charcoal text-sm md:text-base" data-id="element-435">
                                  <span className="text-gold mr-2 mt-0.5 flex-shrink-0" data-id="element-436">
                                    •
                                  </span>
                                  <span data-id="element-437">{benefit}</span>
                                </li>)}
                            </ul>
                          </div>

                          {/* Ideal For */}
                          <div data-id="element-438">
                            <h3 className="font-heading text-lg md:text-xl font-semibold text-navy mb-3 md:mb-4 flex items-center" data-id="element-439">
                              <Users className="h-5 w-5 text-gold mr-2 flex-shrink-0" data-id="element-440" />
                              Ideal For
                            </h3>
                            <ul className="space-y-2 md:space-y-3" data-id="element-441">
                              {service.idealFor.map((client, i) => <li key={i} className="flex items-start text-charcoal text-sm md:text-base" data-id="element-442">
                                  <span className="text-gold mr-2 mt-0.5 flex-shrink-0" data-id="element-443">
                                    •
                                  </span>
                                  <span data-id="element-444">{client}</span>
                                </li>)}
                            </ul>
                          </div>
                        </div>

                        {/* CTA */}
                        <div className="mt-6 md:mt-8 pt-6 md:pt-8 border-t border-gold/20" data-id="element-445">
                          <Button asChild className="bg-gold hover:bg-gold-dark text-navy font-semibold" data-id="element-446">
                            <Link to="/contact" className="inline-flex items-center justify-center gap-2" data-id="element-447">
                              Inquire About This Service
                              <ArrowRight className="h-4 w-4" data-id="element-448" />
                            </Link>
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-navy py-16 md:py-20" data-id="element-449">
        <div className="container mx-auto px-4 lg:px-8" data-id="element-450">
          <motion.div className="max-w-3xl mx-auto text-center" initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-451">
            <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4 md:mb-6" data-id="element-452">
              Ready to Explore Strategic Opportunities?
            </h2>
            <div className="w-20 md:w-24 h-1 bg-gold mx-auto mb-6 md:mb-8" data-id="element-453" />
            <p className="text-base md:text-lg text-steel mb-8 leading-relaxed" data-id="element-454">
              Connect with our team to discuss how we can support your
              investment, mining, or off-take objectives.
            </p>
            <Button asChild size="lg" className="bg-gold hover:bg-gold-dark text-navy font-semibold text-base md:text-lg px-8" data-id="element-455">
              <Link to="/contact" data-id="element-456">Schedule a Consultation</Link>
            </Button>
          </motion.div>
        </div>
      </section>
    </div>;
}